/** The subscription constructor (spec 20261001_subscription_constructor_plan_ru):
 *  the company's set of paid items, its usage, its statements and the public
 *  price book. Money arrives as strings ("19.00") and is only ever formatted,
 *  never re-computed here — the server's quote is the figure that is invoiced. */
import { api } from "$lib/api";
import { formatCompanyInstant, formatDate, formatDayFirst, formatEur } from "$lib/format";

// ── Vocabulary (mirrors backend/apps/billing/catalog.py) ─────────────────────

export type ItemCode =
  | "base" | "m_page" | "m_stock" | "m_acc" | "m_rep" | "m_dash" | "m_find"
  | "bundle" | "nolimit" | "order" | "posting" | "onboarding";

/** The optional modules, in the order the constructor lists them. */
export const MODULE_CODES = ["m_page", "m_stock", "m_acc", "m_rep", "m_dash", "m_find"] as const;
/** A package replaces the base and every module: it is bought alone. */
export const PACKAGE_CODES = ["bundle", "nolimit"] as const;
export const SUBSCRIBABLE_CODES: string[] = ["base", ...MODULE_CODES, ...PACKAGE_CODES];

/** The feature each module opens — what `/me` entitlements are keyed by. */
export type ModuleFeature =
  | "public_page" | "warehouse" | "accounting" | "reports" | "dashboard_mgmt" | "partner_search";
export type SellerFeature = "publish_catalog" | "sales_pricing" | "accept_orders" | "pricing";
export type PaidFeature = ModuleFeature | SellerFeature;

export const MODULE_FEATURES: ModuleFeature[] = [
  "public_page", "warehouse", "accounting", "reports", "dashboard_mgmt", "partner_search",
];

/** Which price-list item sells a feature — the constructor preselects it when
 *  a locked door is opened. The overview carries the same map from the
 *  server (`feature_codes`); this copy serves the screens that have not
 *  loaded it. */
export const FEATURE_CODE: Record<PaidFeature, ItemCode> = {
  publish_catalog: "base",
  sales_pricing: "base",
  accept_orders: "base",
  pricing: "base",
  public_page: "m_page",
  warehouse: "m_stock",
  accounting: "m_acc",
  reports: "m_rep",
  dashboard_mgmt: "m_dash",
  partner_search: "m_find",
};

export type PresetId = "solo" | "mini" | "team" | "giant";
export const PRESET_IDS: PresetId[] = ["solo", "mini", "team", "giant"];
export const PERIODS = [1, 3, 6, 12] as const;

// ── Shapes ──────────────────────────────────────────────────────────────────

export type PriceBookItem = {
  code: ItemCode;
  kind: "base" | "module" | "package" | "metered" | "service";
  price: string;
  /** null = no meter (unlimited). */
  included_orders: number | null;
  included_postings: number | null;
  grants: string[];
};

export type SubscriptionItem = {
  code: ItemCode; unit_price: string; started_on: string; ends_on: string | null;
};

export type SubscriptionState = {
  plan: "free" | "pro" | "custom";
  status: "active" | "trialing" | "past_due" | "canceled";
  is_pro: boolean;
  trial_ends_at: string | null;
  payment_mode: "prepaid" | "monthly";
  period_months: number;
  current_period_start: string | null;
  current_period_end: string | null;
  auto_renew: boolean;
  cancel_at_period_end: boolean;
  paused: boolean;
  paused_until: string | null;
  /** The first day a new pause may start; null = now. */
  pause_available_on: string | null;
  pause_max_months: number;
  discount_percent: number | string;
  /** Last day already paid for (custom plan only). */
  paid_through: string | null;
  items: SubscriptionItem[];
};

export type Metered = {
  count: number;
  included: number | null;
  unlimited: boolean;
  over: number;
  unit_price: string;
  amount: string;
  charged: boolean;
};

export type Quote = {
  codes: ItemCode[];
  lines: { code: ItemCode; price: string }[];
  period_months: number;
  discount_percent: string;
  fixed: string;
  fixed_net: string;
  orders: Metered;
  postings: Metered;
  usage: string;
  total: string;
  prepay_total: string;
};

export type Offer = { key: "own" | "bundle" | "nolimit"; total: string; best: boolean; quote: Quote };
export type QuoteResult = { quote: Quote; offers: Offer[] };

export type UsageFact = { key: string; count: number };
export type ModuleUse = { code: ItemCode; used: boolean; facts: UsageFact[] };

export type Usage = {
  period: string; // "YYYY-MM"
  start: string;
  end: string;
  metered: {
    orders: number;
    by_channel: { client: number; quick: number; seller: number };
    postings: number;
  };
  modules: ModuleUse[];
  codes: ItemCode[];
  quote: Quote;
  offers: Offer[];
};

export type CompanySize = {
  premises_area: string; annual_turnover: string; staff_count: string; monthly_orders: string;
};

export type Recap = {
  show: "pre" | "end" | null;
  term_ends_at: string | null;
  term_ended_at: string | null;
  days_left: number | null;
};

export type Overview = {
  subscription: SubscriptionState;
  price_book: PriceBookItem[];
  period_discounts: Record<string, number>;
  presets: Record<PresetId, ItemCode[]>;
  feature_codes: Record<string, ItemCode>;
  usage: Usage;
  size: CompanySize;
  recommended: { preset: PresetId; codes: ItemCode[] } | null;
  recap: Recap;
  open_statements: number;
};

export type StatementLine = {
  code: string; quantity: string | number; unit_price: string; discount_percent: string | number; amount: string;
};
export type Statement = {
  id: number;
  number: string;
  kind: "period" | "month" | "service";
  period_start: string | null;
  period_end: string | null;
  issued_on: string;
  due_on: string;
  subtotal: string;
  vat_rate: string;
  vat_amount: string;
  total: string;
  status: "issued" | "paid" | "overdue" | "void";
  has_pdf: boolean;
  lines: StatementLine[];
};

export type PaymentMode = "prepaid" | "monthly";

// ── Calls ───────────────────────────────────────────────────────────────────

const base = (slug: string) => `/t/${slug}/billing`;

export const fetchPriceBook = () => api.get<PriceBookItem[]>("/billing/price-book");
export const fetchOverview = (slug: string) => api.get<Overview>(`${base(slug)}/overview`);
export const fetchUsage = (slug: string, period?: string) =>
  api.get<Usage>(`${base(slug)}/usage${period ? `?period=${encodeURIComponent(period)}` : ""}`);
export const fetchQuote = (
  slug: string,
  body: { codes: string[]; period_months: number; orders: number; postings: number },
) => api.post<QuoteResult>(`${base(slug)}/quote`, body);
export const subscribe = (
  slug: string,
  body: { codes: string[]; payment_mode: PaymentMode; period_months: number },
) => api.post<Overview>(`${base(slug)}/subscribe`, body);
export const switchItem = (slug: string, code: string, on: boolean) =>
  api.post<Overview>(`${base(slug)}/items/${encodeURIComponent(code)}/${on ? "on" : "off"}`);
export const pauseSubscription = (slug: string, months: number) =>
  api.post<Overview>(`${base(slug)}/pause`, { months });
export const resumeSubscription = (slug: string) => api.post<Overview>(`${base(slug)}/resume`);
export const cancelSubscription = (slug: string) => api.post<Overview>(`${base(slug)}/cancel`);
export const reactivateSubscription = (slug: string) => api.post<Overview>(`${base(slug)}/reactivate`);
export const fetchStatements = (slug: string) => api.get<Statement[]>(`${base(slug)}/statements`);
/** A plain GET returning the PDF — usable as a download href as-is. */
export const statementPdfUrl = (slug: string, id: number) => `/api/v1${base(slug)}/statements/${id}/pdf`;

/** The constructor, with an item (or a whole set) ticked on arrival. */
export function constructorHref(slug: string, add?: string | null): string {
  return `#/t/${slug}/pricing${add ? `?add=${encodeURIComponent(add)}` : ""}`;
}

// ── Formatting and labels ───────────────────────────────────────────────────

/** "19.00" → «€ 19,00» in the interface language — the app's one EUR format. */
export function money(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "-";
  return formatEur(value);
}

/** Interface keys of each price-list item. Literal keys, so the i18n check
 *  can see every one of them. */
export const ITEM_NAME_KEY: Record<ItemCode, string> = {
  base: "billing.item.base.name",
  m_page: "billing.item.m_page.name",
  m_stock: "billing.item.m_stock.name",
  m_acc: "billing.item.m_acc.name",
  m_rep: "billing.item.m_rep.name",
  m_dash: "billing.item.m_dash.name",
  m_find: "billing.item.m_find.name",
  bundle: "billing.item.bundle.name",
  nolimit: "billing.item.nolimit.name",
  order: "billing.item.order.name",
  posting: "billing.item.posting.name",
  onboarding: "billing.item.onboarding.name",
};
export const ITEM_DESC_KEY: Record<ItemCode, string> = {
  base: "billing.item.base.desc",
  m_page: "billing.item.m_page.desc",
  m_stock: "billing.item.m_stock.desc",
  m_acc: "billing.item.m_acc.desc",
  m_rep: "billing.item.m_rep.desc",
  m_dash: "billing.item.m_dash.desc",
  m_find: "billing.item.m_find.desc",
  bundle: "billing.item.bundle.desc",
  nolimit: "billing.item.nolimit.desc",
  order: "billing.item.order.desc",
  posting: "billing.item.posting.desc",
  onboarding: "billing.item.onboarding.desc",
};

/** One key per usage counter of catalog.USAGE_KEYS — «12 приходов». */
export const FACT_KEY: Record<string, string> = {
  "sell.access": "billing.fact.sell.access",
  "sell.pricelist": "billing.fact.sell.pricelist",
  "sell.document": "billing.fact.sell.document",
  "page.view": "billing.fact.page.view",
  "page.request": "billing.fact.page.request",
  "stock.receipt": "billing.fact.stock.receipt",
  "stock.adjust": "billing.fact.stock.adjust",
  "acc.open": "billing.fact.acc.open",
  "acc.export": "billing.fact.acc.export",
  "acc.statement": "billing.fact.acc.statement",
  "rep.run": "billing.fact.rep.run",
  "rep.export": "billing.fact.rep.export",
  "dash.dynamics": "billing.fact.dash.dynamics",
  "dash.plan": "billing.fact.dash.plan",
  "find.incoming": "billing.fact.find.incoming",
  "find.request": "billing.fact.find.request",
};

export const PRESET_NAME_KEY: Record<PresetId, string> = {
  solo: "billing.preset.solo.name",
  mini: "billing.preset.mini.name",
  team: "billing.preset.team.name",
  giant: "billing.preset.giant.name",
};
export const PRESET_WHO_KEY: Record<PresetId, string> = {
  solo: "billing.preset.solo.who",
  mini: "billing.preset.mini.who",
  team: "billing.preset.team.who",
  giant: "billing.preset.giant.who",
};

/** A billing date: a bare day ("2026-10-31") as that day, an instant in the
 *  company's timezone; dd.mm.yyyy either way. */
export function billingDate(value: string | null | undefined, slug: string): string {
  if (!value) return "-";
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? formatDate(value) : formatCompanyInstant(value, slug);
}

/** "2026-10" → «октябрь 2026» in the interface language. */
export function monthLabel(period: string): string {
  const [y, m] = period.split("-").map(Number);
  return formatDayFirst(new Date(Date.UTC(y, m - 1, 15)), { month: "long", year: "numeric" });
}

export const itemName = (t: (key: string) => string, code: string) =>
  ITEM_NAME_KEY[code as ItemCode] ? t(ITEM_NAME_KEY[code as ItemCode]) : code;

// ── Selection rules ─────────────────────────────────────────────────────────

/** Read `?add=` / `?codes=` into a set: unknown codes are dropped, a package
 *  stands alone, and a package plus anything else keeps the rest. */
export function cleanSelection(codes: string[]): ItemCode[] {
  const known = [...new Set(codes.map((c) => c.trim()).filter((c) => SUBSCRIBABLE_CODES.includes(c)))];
  const packages = known.filter((c) => (PACKAGE_CODES as readonly string[]).includes(c));
  if (packages.length && known.length === packages.length) return [packages[packages.length - 1] as ItemCode];
  return known.filter((c) => !(PACKAGE_CODES as readonly string[]).includes(c)) as ItemCode[];
}

// ── Locked doors on screen ──────────────────────────────────────────────────
/* A page that already shows a module's lock screen does not need the upgrade
   dialog on top of it when its own loads come back 402: ModuleGate records the
   features it is standing in for, UpgradeModal skips those. */
const gatesShown = new Map<string, number>();
export function markGateShown(feature: string): () => void {
  gatesShown.set(feature, (gatesShown.get(feature) ?? 0) + 1);
  return () => {
    const n = (gatesShown.get(feature) ?? 1) - 1;
    if (n <= 0) gatesShown.delete(feature);
    else gatesShown.set(feature, n);
  };
}
export const isGateShown = (feature: string | undefined) => !!feature && gatesShown.has(feature);
